"use client";

import { useState, useEffect } from "react";
import { type UseEmblaCarouselType } from "embla-carousel-react";
import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import Image, { GroqImage } from "@/helpers/Image";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import dots from "@/public/payout/dots.png";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface CertificateSectionData {
  chip?: string;
  title?: PortableTextBlock[];
  text?: PortableTextBlock[];
  highlightedText?: string;
  images?: GROQImage[];
}

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  active: {
    opacity: 1,
    transform: "translateY(0%) translateX(0%) translateZ(0px) scale(1)",
    zIndex: 10,
    pointerEvents: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  },
  next: {
    opacity: 1,
    transform: "translateY(0%) translateX(50%) translateZ(-50px) scale(0.7)",
    zIndex: 9,
    pointerEvents: "none",
  },
  prev: {
    opacity: 1,
    transform: "translateY(0%) translateX(-50%) translateZ(-50px) scale(0.7)",
    zIndex: 9,
    pointerEvents: "none",
  },
  "bg-next": {
    opacity: 0,
    transform: "translateY(0%) translateX(0%) translateZ(-150px) scale(0.76)",
    zIndex: 1,
    pointerEvents: "none",
  },
  "bg-prev": {
    opacity: 0,
    transform: "translateY(0%) translateX(0%) translateZ(-150px) scale(0.76)",
    zIndex: 1,
    pointerEvents: "none",
  },
  hidden: {
    opacity: 0,
    transform: "translateY(0%) translateX(0%) translateZ(-150px) scale(0.76)",
    zIndex: 1,
    pointerEvents: "none",
  },
};

function getStatus(diff: number, len: number): string {
  if (diff === 0) return "active";
  if (diff === 1 || (len === 2 && diff === -1)) return "next";
  if (diff === -1) return "prev";
  if (diff === 2 || (len === 4 && diff === -2)) return "bg-next";
  if (diff === -2) return "bg-prev";
  return "hidden";
}

type EmblaApi = UseEmblaCarouselType[1];

function ImagesCarousel({ images }: { images: GROQImage[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileApi, setMobileApi] = useState<EmblaApi>(undefined);
  const [mobileIndex, setMobileIndex] = useState(0);
  const len = images.length;

  useEffect(() => {
    if (!mobileApi) return;
    const onSelect = () => setMobileIndex(mobileApi.selectedScrollSnap());
    mobileApi.on("select", onSelect);
    return () => {
      mobileApi.off("select", onSelect);
    };
  }, [mobileApi]);

  const navigate = (dir: 1 | -1) =>
    setActiveIndex((i) => (i + dir + len) % len);

  if (len === 0) return null;

  if (len === 1) {
    return (
      <div className="my-[8rem] flex justify-center w-full">
        <div className="w-full lg:w-[60%] aspect-[4/3] overflow-hidden rounded-[0.25rem] bg-[#F0F4F2]">
          <GroqImage
            src={images[0]}
            alt={images[0].name || ""}
            className="!w-full !h-full object-cover"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative my-[3rem] lg:my-[8rem] w-full">
      {/* Mobile */}
      <div className="lg:hidden">
        <Carousel opts={{ align: "center", loop: true }} setApi={setMobileApi}>
          <CarouselContent>
            {images.map((img, i) => (
              <CarouselItem key={i} className="basis-[85%] px-[1rem]">
                <div className="aspect-[4/3] overflow-hidden rounded-[0.25rem] bg-[#F0F4F2]">
                  <GroqImage
                    src={img}
                    alt={img.name || ""}
                    className="!w-full !h-full object-cover rounded-[0.5rem]"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="mt-[3rem] flex items-center justify-center gap-[1.5rem]">
          <div className="flex-1 h-[0.2rem] rounded-full bg-[#D1D5DB] overflow-hidden">
            <div
              className="h-full bg-black rounded-full transition-all duration-300"
              style={{ width: `${((mobileIndex + 1) / len) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div
          className="relative mx-auto w-[55%]"
          style={{ perspective: "1200px" }}
        >
          <div className="relative w-full" style={{ paddingBottom: "75%" }}>
            {images.map((img, i) => {
              let diff = i - activeIndex;
              if (diff > len / 2) diff -= len;
              else if (diff < -len / 2) diff += len;
              const status = getStatus(diff, len);
              return (
                <div
                  key={i}
                  className="absolute inset-0 overflow-hidden rounded-[0.25rem] bg-[#F0F4F2]"
                  style={{
                    ...STATUS_STYLES[status],
                    transition: "all 0.6s cubic-bezier(0.65, 0, 0.35, 1)",
                    backfaceVisibility: "hidden",
                    willChange: "transform, opacity",
                  }}
                >
                  <GroqImage
                    src={img}
                    alt={img.name || ""}
                    className="!w-full !h-full object-fill rounded-[1rem]"
                    quality={100}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-[3rem] flex justify-center gap-[1.5rem]">
          <button
            onClick={() => navigate(-1)}
            aria-label="Previous image"
            className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-black text-white transition-opacity hover:opacity-70"
          >
            <svg
              className="w-[2.5rem] h-[2.5rem]"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            onClick={() => navigate(1)}
            aria-label="Next image"
            className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-black text-white transition-opacity hover:opacity-70"
          >
            <svg
              className="w-[2.5rem] h-[2.5rem]"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function DotArrow({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      viewBox="0 0 32 32"
      fill="none"
    >
      <rect
        x="4.88269"
        y="4.87769"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="4.88269"
        y="24.3918"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="9.75452"
        y="9.75708"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="9.75452"
        y="19.5142"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="9.75452"
        y="4.87769"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="9.75452"
        y="24.3918"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="14.6371"
        y="9.75708"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="14.6371"
        y="19.5142"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="14.6371"
        y="14.6348"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="14.6371"
        y="4.87769"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="14.6371"
        y="24.3918"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="19.509"
        y="9.75708"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="19.509"
        y="19.5142"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="19.509"
        y="14.6348"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
      <rect
        x="24.3906"
        y="14.6348"
        width="2.43913"
        height="2.43913"
        rx="0.487827"
        fill="white"
      />
    </svg>
  );
}

export default function CertificateSection({
  data,
}: {
  data: CertificateSectionData;
}) {
  if (!data.title && !data.images?.length) return null;

  return (
    <section className="w-full bg-white py-[8rem] lg:py-[16rem] relative z-10">
      <Image
        src={dots}
        alt=""
        className="absolute inset-0 !w-full object-cover mix-blend-exclusion hidden lg:block"
      />
      <div className="mx-auto flex w-[min(141rem,100%)] flex-col items-center gap-[6rem] lg:gap-[10rem] px-[2rem] lg:px-0">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center text-center w-full"
        >
          {data.chip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black/70">
              {data.chip}
            </p>
          )}
          {data.title && (
            <div className="richtext prose-headings:mb-[1.75rem] prose-headings:text-black max-w-[85rem]">
              <PortableText value={data.title} components={richTextComponent} />
            </div>
          )}
          {data.images && data.images.length > 0 && (
            <ImagesCarousel images={data.images} />
          )}
          {data.text && (
            <div className="mb-[5.5rem] richtext prose-p:text-[2rem] prose-p:leading-[1.6] prose-p:text-black/80 max-w-[46.375rem]">
              <PortableText value={data.text} components={richTextComponent} />
            </div>
          )}
          {data.highlightedText && (
            <div className="bg-black px-[4.5rem] py-[3rem] flex items-center gap-[5rem]">
              <DotArrow className="w-[3.964rem] h-[3.964rem]" />
              <p className="mb-0 font-geist text-[2.25rem] leading-[1] text-white font-semibold">
                {data.highlightedText}
              </p>
              <DotArrow className="w-[3.964rem] h-[3.964rem] rotate-180" />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
